import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { TestModeToggle } from "@/components/admin/TestModeToggle";
import { ApproachSyncButton } from "@/components/admin/ApproachSyncButton";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DiagAssessmentPanel } from "@/components/admin/DiagAssessmentPanel";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { DAYS, BLOCKS, slotLabel } from "@/lib/slots";
import { SignOut, ArrowSquareOut, Trash, FilePdf } from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending review" },
  { value: "accepted", label: "Accepted" },
  { value: "prep_required", label: "Prep required" },
  { value: "declined", label: "Declined" },
];

const STATUS_COLOR = {
  pending: "bg-cvc-parchment text-cvc-ink border-cvc-border",
  accepted: "bg-scaled-green text-white border-scaled-green",
  prep_required: "bg-cvc-red/15 text-cvc-red border-cvc-red/30",
  declined: "bg-cvc-ink/15 text-cvc-ink-soft border-cvc-ink/20",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["signups", "waiting", "availability", "content", "diag"].includes(t) ? t : "signups";
  });
  const [signups, setSignups] = useState([]);
  const [availability, setAvailability] = useState({});
  const [content, setContent] = useState({});
  const [savingAvail, setSavingAvail] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const [detail, setDetail] = useState(null);

  const loadAll = async () => {
    try {
      const [s, a, c] = await Promise.all([
        api.get("/admin/signups"),
        api.get("/admin/availability"),
        api.get("/content"),
      ]);
      setSignups(s.data);
      setAvailability(a.data);
      setContent(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load data");
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once on mount
  const waitingList = useMemo(() => signups.filter((s) => s.waiting_list && !s.confirmed_slot), [signups]);
  const contentKeys = useMemo(() => Object.keys(content).sort(), [content]);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const toggleSlot = (key) => setAvailability((a) => ({ ...a, [key]: !a[key] }));

  const saveAvailability = async () => {
    setSavingAvail(true);
    try {
      const { data } = await api.put("/admin/availability", { slots: availability });
      setAvailability(data);
      toast.success("Calendar updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setSavingAvail(false); }
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      const { data } = await api.put("/admin/content", { content });
      setContent(data);
      toast.success("Landing page content saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setSavingContent(false); }
  };

  const updateStatus = async (id, status, admin_notes = "") => {
    try {
      const { data } = await api.patch(`/admin/signups/${id}`, { status, admin_notes });
      setSignups((all) => all.map((s) => (s.id === id ? data : s)));
      if (detail?.id === id) setDetail(data);
      toast.success("Signup updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    }
  };

  const deleteSignup = async (id) => {
    if (!confirm("Permanently delete this signup?")) return;
    try {
      await api.delete(`/admin/signups/${id}`);
      setSignups((all) => all.filter((s) => s.id !== id));
      setDetail(null);
      toast.success("Signup deleted.");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const createRetainerCheckout = async (signupId) => {
    try {
      const { data } = await api.post("/admin/checkout/retainer", {
        signup_id: signupId,
        origin_url: window.location.origin,
      });
      try { await navigator.clipboard.writeText(data.url); } catch (err) { console.warn("Clipboard unavailable; link opened in a new tab instead.", err?.message); }
      toast.success("Retainer payment link created (copied to clipboard).");
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create checkout");
    }
  };

  const createOnboardingCheckout = async (signupId, opts = {}) => {
    try {
      const payload = {
        signup_id: signupId,
        origin_url: window.location.origin,
      };
      if (opts.smoke) payload.test_cents = 100;  // $1+$1 instead of real tier amounts
      const { data } = await api.post("/admin/checkout/subscription", payload);
      try { await navigator.clipboard.writeText(data.url); } catch (err) { console.warn("Clipboard unavailable; link opened in a new tab instead.", err?.message); }
      toast.success(opts.smoke ? "SMOKE onboarding link created ($1+$1)." : "Onboarding checkout link created (copied to clipboard).");
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create onboarding checkout");
    }
  };


  const promptAssignCustomSlot = async (signup) => {
    const example = "e.g., Sat Mar 14, 2026 — 6:30 PM at the shop";
    const value = window.prompt(
      `Assign a custom meeting slot for ${signup.company_name}.\nThis can be any time (evenings, Saturdays, lunch — anything not on the standard grid).\n\n${example}`,
      signup.confirmed_slot || ""
    );
    if (value === null) return;  // cancelled
    const slot = value.trim();
    if (!slot) { toast.error("Slot can't be empty."); return; }
    try {
      const { data } = await api.post(`/admin/signups/${signup.id}/assign-slot`, {
        confirmed_slot: slot, auto_accept: true,
      });
      toast.success(`Slot assigned. ${signup.company_name} is now accepted at: ${slot}`);
      // refresh list + detail
      await loadAll();
      if (detail?.id === signup.id) setDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to assign slot");
    }
  };

  // Lock one of the prospect's pre-chosen preferred slots — same endpoint, just pre-fills
  // the slot key from the signup record. Auto-accepts the prospect if not already accepted.
  const lockPreferredSlot = async (signup, slotKey) => {
    const label = slotLabel(slotKey);
    if (!window.confirm(`Lock "${label}" as the appointment for ${signup.company_name}? This auto-accepts them and sends the acceptance email.`)) return;
    try {
      const { data } = await api.post(`/admin/signups/${signup.id}/assign-slot`, {
        confirmed_slot: slotKey, auto_accept: true,
      });
      toast.success(`Locked ${label} for ${signup.company_name}.`);
      await loadAll();
      if (detail?.id === signup.id) setDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to lock slot");
    }
  };

  const downloadContractPdf = async (signupId, companyName) => {
    try {
      const res = await fetch(`${API}/admin/signups/${signupId}/contract.pdf`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (companyName || "client").replace(/[^a-z0-9-_]+/gi, "_").slice(0, 48);
      a.download = `CVC-Contract-${safe}-${signupId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Contract PDF downloaded.");
    } catch (e) {
      toast.error("Could not download contract PDF.");
    }
  };

  return (
    <div className="min-h-screen paper-grain">
      <header className="bg-cvc-navy text-cvc-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-body font-semibold text-cvc-cream/60">Cache Valley Consulting</p>
            <h1 className="font-heading text-2xl">Admin Portal</h1>
          </div>
          <div className="flex items-center gap-3">
            <TestModeToggle />
            <span className="font-body text-sm text-cvc-cream/80 hidden sm:inline">{user?.email}</span>
            <a href="/" className="hidden sm:inline-flex items-center gap-1 font-body text-xs text-cvc-cream/70 hover:text-cvc-cream"><ArrowSquareOut size={14} /> Site</a>
            <Button data-testid="admin-logout" variant="ghost" onClick={doLogout} className="text-cvc-cream hover:bg-cvc-cream/10 hover:text-cvc-cream rounded-full">
              <SignOut size={16} className="mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <TabsList className="bg-white border border-cvc-border" data-testid="admin-tabs">
              <TabsTrigger data-testid="tab-signups" value="signups" className="data-[state=active]:bg-cvc-navy data-[state=active]:text-cvc-cream font-body">Signups ({signups.length})</TabsTrigger>
              <TabsTrigger data-testid="tab-waiting" value="waiting" className="data-[state=active]:bg-cvc-navy data-[state=active]:text-cvc-cream font-body">
                Waiting list ({signups.filter((s) => s.waiting_list && !s.confirmed_slot).length})
              </TabsTrigger>
              <TabsTrigger data-testid="tab-availability" value="availability" className="data-[state=active]:bg-cvc-navy data-[state=active]:text-cvc-cream font-body">Availability</TabsTrigger>
              <TabsTrigger data-testid="tab-content" value="content" className="data-[state=active]:bg-cvc-navy data-[state=active]:text-cvc-cream font-body">Landing Copy</TabsTrigger>
              <TabsTrigger data-testid="tab-diag" value="diag" className="data-[state=active]:bg-cvc-navy data-[state=active]:text-cvc-cream font-body">Diag &amp; Assessment</TabsTrigger>
            </TabsList>
            <ApproachSyncButton />
          </div>

          <TabsContent value="diag" className="mt-6">
            <DiagAssessmentPanel />
          </TabsContent>

          <TabsContent value="signups" className="mt-6">
            <div className="rounded-2xl bg-white border border-cvc-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-body">
                  <thead className="bg-cvc-parchment text-cvc-ink-soft uppercase text-xs tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-3">Company</th>
                      <th className="text-left px-5 py-3">Owner</th>
                      <th className="text-left px-5 py-3">Tier</th>
                      <th className="text-left px-5 py-3">Slots</th>
                      <th className="text-left px-5 py-3">Status</th>
                      <th className="text-left px-5 py-3">Submitted</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {signups.length === 0 && (
                      <tr><td colSpan={7} className="px-5 py-10 text-center text-cvc-ink-soft">No signups yet.</td></tr>
                    )}
                    {signups.map((s) => (
                      <tr key={s.id} data-testid={`signup-row-${s.id}`} className="border-t border-cvc-border hover:bg-cvc-parchment/40">
                        <td className="px-5 py-3 text-cvc-ink font-medium">{s.company_name}</td>
                        <td className="px-5 py-3 text-cvc-ink-soft">{s.owner_name}<br /><span className="text-xs">{s.email}</span></td>
                        <td className="px-5 py-3 text-cvc-ink-soft">{s.tier === "2_meetings" ? "2/mo" : "3/mo"} · ${(s.monthly_budget ?? 0).toLocaleString()}</td>
                        <td className="px-5 py-3 text-cvc-ink-soft text-xs">{s.waiting_list ? "Waiting list" : (s.preferred_slots || []).map(slotLabel).join(", ") || "—"}</td>
                        <td className="px-5 py-3">
                          <Badge className={`${STATUS_COLOR[s.status]} border font-body`}>{STATUS_OPTIONS.find(x => x.value === s.status)?.label}</Badge>
                        </td>
                        <td className="px-5 py-3 text-cvc-ink-soft text-xs">{new Date(s.created_at).toLocaleString()}</td>
                        <td className="px-5 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              data-testid={`download-pdf-${s.id}`}
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadContractPdf(s.id, s.company_name)}
                              className="text-cvc-navy hover:text-cvc-red"
                              title="Download signed contract PDF"
                            >
                              <FilePdf size={16} weight="duotone" />
                            </Button>
                            <Button data-testid={`view-signup-${s.id}`} size="sm" variant="ghost" onClick={() => setDetail(s)} className="text-cvc-navy">Review →</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="waiting" className="mt-6">
            <div className="rounded-2xl bg-white border border-cvc-border overflow-hidden">
              <div className="p-7 border-b border-cvc-border">
                <h2 className="font-heading text-2xl text-cvc-navy">Waiting list</h2>
                <p className="font-body text-sm text-cvc-ink-soft mt-1">
                  Prospects who couldn't (or chose not to) lock into one of the standard meeting windows. You can fit them into any time you'd like — including evenings, Saturdays, or lunch — using "Assign custom slot".
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-body">
                  <thead className="bg-cvc-parchment text-cvc-ink-soft uppercase text-xs tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-3">Company</th>
                      <th className="text-left px-5 py-3">Owner</th>
                      <th className="text-left px-5 py-3">Tier</th>
                      <th className="text-left px-5 py-3">Submitted</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitingList.length === 0 && (
                      <tr><td colSpan={5} className="px-5 py-10 text-center text-cvc-ink-soft">Nobody on the waiting list right now.</td></tr>
                    )}
                    {waitingList.map((s) => (
                      <tr key={s.id} data-testid={`waiting-row-${s.id}`} className="border-t border-cvc-border hover:bg-cvc-parchment/40">
                        <td className="px-5 py-3 text-cvc-ink font-medium">{s.company_name}</td>
                        <td className="px-5 py-3 text-cvc-ink-soft">{s.owner_name}<br /><span className="text-xs">{s.email}</span></td>
                        <td className="px-5 py-3 text-cvc-ink-soft">{s.tier === "2_meetings" ? "2/mo" : "3/mo"} · ${(s.monthly_budget ?? 0).toLocaleString()}</td>
                        <td className="px-5 py-3 text-cvc-ink-soft text-xs">{new Date(s.created_at).toLocaleString()}</td>
                        <td className="px-5 py-3 text-right">
                          <Button
                            data-testid={`assign-custom-slot-${s.id}`}
                            size="sm"
                            onClick={() => promptAssignCustomSlot(s)}
                            className="bg-cvc-navy hover:bg-cvc-navy-deep text-white rounded-full px-4"
                          >
                            Assign custom slot
                          </Button>
                          <Button
                            data-testid={`open-detail-${s.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => setDetail(s)}
                            className="ml-1 text-cvc-ink-soft"
                          >
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="availability" className="mt-6">
            <div className="rounded-2xl bg-white border border-cvc-border p-7">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-heading text-2xl text-cvc-navy">Available meeting windows</h2>
                  <p className="font-body text-sm text-cvc-ink-soft mt-1">Toggle which time blocks prospective clients can request. Green = open to new signups.</p>
                </div>
                <Button data-testid="save-availability" onClick={saveAvailability} disabled={savingAvail} className="bg-cvc-red hover:bg-cvc-red-deep text-white rounded-full px-6">
                  {savingAvail ? "Saving…" : "Save calendar"}
                </Button>
              </div>

              <div className="mt-7 overflow-x-auto">
                <table className="w-full border-separate border-spacing-2">
                  <thead>
                    <tr>
                      <th></th>
                      {DAYS.map(d => <th key={d.key} className="font-body text-xs uppercase tracking-wider text-cvc-ink-soft pb-2">{d.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {BLOCKS.map(b => (
                      <tr key={b.key}>
                        <td className="text-right pr-3 align-middle">
                          <div className="font-body text-xs uppercase tracking-wider text-cvc-ink-soft">{b.label}</div>
                          <div className="font-body text-[10px] text-cvc-ink-soft/70">{b.hint}</div>
                        </td>
                        {DAYS.map(d => {
                          const k = `${d.key}-${b.key}`;
                          const on = !!availability[k];
                          return (
                            <td key={k}>
                              <button
                                data-testid={`admin-slot-${k}`}
                                onClick={() => toggleSlot(k)}
                                className={`w-full h-14 rounded-xl border-2 font-body text-xs transition-all ${
                                  on ? "bg-scaled-green border-scaled-green text-white" : "bg-cvc-parchment border-cvc-border text-cvc-ink-soft hover:border-cvc-navy"
                                }`}
                              >
                                {on ? "Open" : "Closed"}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          </TabsContent>

          <TabsContent value="content" className="mt-6">
            <div className="rounded-2xl bg-white border border-cvc-border p-7">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-heading text-2xl text-cvc-navy">Landing page copy</h2>
                  <p className="font-body text-sm text-cvc-ink-soft mt-1">Edit headlines, taglines, and contact text shown on the public landing page.</p>
                </div>
                <Button data-testid="save-content" onClick={saveContent} disabled={savingContent} className="bg-cvc-red hover:bg-cvc-red-deep text-white rounded-full px-6">
                  {savingContent ? "Saving…" : "Save copy"}
                </Button>
              </div>

              <div className="mt-6 grid lg:grid-cols-2 gap-5">
                {contentKeys.map((key) => (
                  <div key={key} className="rounded-xl border border-cvc-border p-4 bg-cvc-cream">
                    <Label className="font-body text-xs uppercase tracking-wider text-cvc-red">{key.replace(/_/g, " ")}</Label>
                    {content[key].length > 60 ? (
                      <Textarea
                        data-testid={`content-${key}`}
                        value={content[key]}
                        onChange={(e) => setContent({ ...content, [key]: e.target.value })}
                        className="mt-2 bg-white border-cvc-border min-h-[120px]"
                      />
                    ) : (
                      <Input
                        data-testid={`content-${key}`}
                        value={content[key]}
                        onChange={(e) => setContent({ ...content, [key]: e.target.value })}
                        className="mt-2 bg-white border-cvc-border"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl bg-cvc-cream max-h-[92vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-3xl text-cvc-navy">{detail.company_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4 text-sm font-body">
                  <div><p className="text-xs uppercase tracking-wider text-cvc-ink-soft">Owner</p><p className="text-cvc-ink">{detail.owner_name}{detail.owner_title && `, ${detail.owner_title}`}</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-cvc-ink-soft">Email</p><p className="text-cvc-ink">{detail.email}</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-cvc-ink-soft">Phone</p><p className="text-cvc-ink">{detail.phone}</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-cvc-ink-soft">Tier</p><p className="text-cvc-ink">{detail.tier === "2_meetings" ? `Scaling Consulting — 2 meetings/mo · $${(detail.monthly_budget ?? 0).toLocaleString()}/mo` : `Scaling at High-Growth Pace — 3 meetings/mo · $${(detail.monthly_budget ?? 0).toLocaleString()}/mo`}</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-cvc-ink-soft">First+Last retainer</p><p className="text-cvc-ink">${detail.first_last_retainer?.toLocaleString()}</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-cvc-ink-soft">Signed on</p><p className="text-cvc-ink">{new Date(detail.created_at).toLocaleString()}</p></div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wider text-cvc-ink-soft">Preferred meeting windows</p>
                  {detail.waiting_list ? (
                    <p className="text-cvc-ink font-body text-sm mt-1">Soonest-next-availability waiting list</p>
                  ) : (detail.preferred_slots || []).length === 0 ? (
                    <p className="text-cvc-ink font-body text-sm mt-1">—</p>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(detail.preferred_slots || []).map((slotKey) => {
                        const isLocked = detail.confirmed_slot === slotKey;
                        return (
                          <Button
                            key={slotKey}
                            data-testid={`detail-lock-slot-${slotKey}`}
                            size="sm"
                            variant={isLocked ? "default" : "outline"}
                            disabled={isLocked}
                            onClick={() => lockPreferredSlot(detail, slotKey)}
                            className={`rounded-full px-4 text-xs ${isLocked ? "bg-scaled-green hover:bg-scaled-green text-white border-scaled-green cursor-default" : "border-cvc-navy/40 text-cvc-navy hover:bg-cvc-navy hover:text-white"}`}
                          >
                            {isLocked ? `✓ Locked: ${slotLabel(slotKey)}` : `Lock ${slotLabel(slotKey)}`}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                  {detail.confirmed_slot && !(detail.preferred_slots || []).includes(detail.confirmed_slot) && (
                    <p className="mt-2 inline-block px-3 py-1 rounded-full bg-scaled-green/10 border border-scaled-green/30 text-scaled-green-deep text-xs font-body font-semibold" data-testid="detail-confirmed-slot">
                      Appointment locked: {slotLabel(detail.confirmed_slot)}
                    </p>
                  )}
                  <div className="mt-3">
                    <Button
                      data-testid="detail-assign-custom-slot"
                      size="sm"
                      variant="outline"
                      onClick={() => promptAssignCustomSlot(detail)}
                      className="rounded-full px-4 text-xs"
                    >
                      {detail.confirmed_slot ? "Re-assign custom slot" : "Assign custom slot…"}
                    </Button>
                    <span className="ml-2 font-body text-[11px] text-cvc-ink-soft italic">
                      Pick any time — evenings, Saturdays, lunch. Auto-accepts the prospect.
                    </span>
                  </div>
                </div>

                {detail.notes && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-cvc-ink-soft">Notes from owner</p>
                    <p className="text-cvc-ink font-body text-sm mt-1 whitespace-pre-wrap">{detail.notes}</p>
                  </div>
                )}

                <div className="rounded-xl bg-white border border-cvc-border p-4">
                  <p className="text-xs uppercase tracking-wider text-cvc-ink-soft">Digital signature</p>
                  <p className="font-heading italic text-3xl text-cvc-navy mt-1">{detail.signature_typed}</p>
                  <p className="text-xs text-cvc-ink-soft mt-1">T&amp;C accepted: {detail.tc_accepted ? "Yes" : "No"}</p>
                </div>

                <div className="rounded-xl bg-cvc-parchment/60 border border-cvc-border p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-heading text-lg text-cvc-navy flex items-center gap-2">
                        <FilePdf size={20} weight="duotone" className="text-cvc-red" /> Signed contract (PDF)
                      </p>
                      <p className="font-body text-xs text-cvc-ink-soft mt-1">
                        Full ESIGN/UETA-compliant engagement letter with the client's typed signature,
                        IP address, user-agent, and timestamp. Use this PDF as your binding legal record.
                      </p>
                    </div>
                    <Button
                      data-testid="download-contract-pdf"
                      onClick={() => downloadContractPdf(detail.id, detail.company_name)}
                      className="bg-cvc-navy hover:bg-cvc-navy-deep text-white rounded-full px-5 shrink-0"
                    >
                      Download PDF
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="font-body text-xs uppercase tracking-wider text-cvc-ink-soft">Decision</Label>
                  <Select value={detail.status} onValueChange={(v) => updateStatus(detail.id, v, detail.admin_notes)}>
                    <SelectTrigger data-testid="detail-status-select" className="mt-1.5 bg-white border-cvc-border"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="rounded-xl bg-cvc-navy/5 border border-cvc-navy/30 p-4">
                  <p className="font-heading text-lg text-cvc-navy">Stripe — onboarding checkout (one step)</p>
                  <p className="font-body text-xs text-cvc-ink-soft mt-1">
                    Single Stripe link that charges the client for first-month + last-month retainer today (${(detail.first_last_retainer)?.toLocaleString()}), then bills the monthly fee (${(detail.monthly_budget ?? 0).toLocaleString()}) on the same date every month thereafter.
                    {(detail.retainer_paid && detail.subscription_active) && <span className="ml-1 text-scaled-green font-semibold">· Active ✓</span>}
                  </p>
                  <Button
                    data-testid="create-onboarding-checkout"
                    onClick={() => createOnboardingCheckout(detail.id)}
                    disabled={detail.status !== "accepted"}
                    className="mt-3 bg-cvc-navy hover:bg-cvc-navy-deep text-white rounded-full px-5"
                  >
                    {(detail.retainer_paid && detail.subscription_active) ? "Re-generate onboarding link" : "Create onboarding checkout link"}
                  </Button>
                  <Button
                    data-testid="create-onboarding-checkout-smoke"
                    onClick={() => createOnboardingCheckout(detail.id, { smoke: true })}
                    disabled={detail.status !== "accepted"}
                    variant="outline"
                    className="mt-3 ml-2 rounded-full px-4 text-xs border-dashed"
                    title="$1 first-month + $1 retainer, $1/mo thereafter. Smoke test only."
                  >
                    Smoke ($1+$1)
                  </Button>
                  {detail.status !== "accepted" && (
                    <p className="font-body text-xs text-cvc-ink-soft mt-2 italic">Mark this signup as "Accepted" first to enable.</p>
                  )}
                </div>

                <details className="rounded-xl bg-cvc-parchment border border-cvc-border p-4">
                  <summary className="font-body text-xs text-cvc-ink-soft cursor-pointer">Legacy: separate retainer-only link</summary>
                  <p className="font-body text-xs text-cvc-ink-soft mt-2">
                    Generate just the first + last-month retainer (${detail.first_last_retainer?.toLocaleString()}) as a one-off payment. Use only if the client has already been set up on monthly billing through another channel.
                    {detail.retainer_paid && <span className="ml-1 text-scaled-green font-semibold">· Paid ✓</span>}
                  </p>
                  <Button
                    data-testid="create-retainer-checkout"
                    onClick={() => createRetainerCheckout(detail.id)}
                    disabled={detail.status !== "accepted"}
                    variant="outline"
                    className="mt-3 rounded-full px-5"
                  >
                    {detail.retainer_paid ? "Re-generate retainer-only link" : "Create retainer-only link"}
                  </Button>
                </details>

                <div>
                  <Label className="font-body text-xs uppercase tracking-wider text-cvc-ink-soft">Internal notes</Label>
                  <Textarea
                    data-testid="detail-admin-notes"
                    value={detail.admin_notes || ""}
                    onChange={(e) => setDetail({ ...detail, admin_notes: e.target.value })}
                    onBlur={(e) => updateStatus(detail.id, detail.status, e.target.value)}
                    className="mt-1.5 bg-white border-cvc-border min-h-[90px]"
                  />
                </div>

                <div className="flex justify-between pt-2">
                  <Button variant="ghost" onClick={() => deleteSignup(detail.id)} className="text-cvc-red hover:bg-cvc-red/10">
                    <Trash size={16} className="mr-2" /> Delete
                  </Button>
                  <Button onClick={() => setDetail(null)} className="bg-cvc-navy hover:bg-cvc-navy-deep text-white rounded-full px-6">
                    Done
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
